import { useEffect } from "react";
import { Phone, MessageSquare } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import LeadForm from "@/components/LeadForm";
import Disclosure from "@/components/Disclosure";
import { BUSINESS_PHONE, BUSINESS_PHONE_FORMATTED, BRAND } from "@/lib/constants";

// Generic landing page used for each customer segment & SEO page.
export default function SegmentPage({
    testid,
    chip,
    title,
    highlight,
    subtitle,
    bullets = [],
    initial = {},
    source = "website",
    imageUrl,
    seoTitle,
    seoDescription,
}) {
    useEffect(() => {
        if (seoTitle) document.title = `${seoTitle} · ${BRAND}`;
        if (seoDescription) {
            let meta = document.querySelector('meta[name="description"]');
            if (!meta) {
                meta = document.createElement("meta");
                meta.setAttribute("name", "description");
                document.head.appendChild(meta);
            }
            meta.setAttribute("content", seoDescription);
        }
    }, [seoTitle, seoDescription]);

    return (
        <div className="min-h-screen" data-testid={testid}>
            <Navbar />

            <section className="relative overflow-hidden">
                <div className="hero-glow -top-20 -left-20" />
                <div className="absolute inset-0 spark-pattern opacity-25 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-10 grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7">
                        <span className="chip reveal">{chip}</span>
                        <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white reveal d1">
                            {title}{" "}
                            {highlight && <span className="accent-gradient-text">{highlight}</span>}
                        </h1>
                        <p className="mt-4 text-lg text-slate-300 max-w-xl reveal d2">
                            {subtitle}
                        </p>
                        {bullets.length > 0 && (
                            <ul className="mt-6 grid sm:grid-cols-2 gap-2 reveal d3">
                                {bullets.map((b, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                        <span className="w-5 h-5 mt-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 inline-flex items-center justify-center text-emerald-300 text-xs">✓</span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="mt-6 flex flex-wrap gap-3 reveal d4">
                            <a href={`tel:${BUSINESS_PHONE}`} className="btn-ghost" data-testid="segment-call-cta">
                                <Phone className="w-4 h-4" /> Call {BUSINESS_PHONE_FORMATTED}
                            </a>
                            <a href={`sms:${BUSINESS_PHONE}?&body=Text me my electricity options`} className="btn-primary" data-testid="segment-text-cta">
                                <MessageSquare className="w-4 h-4" /> Text me for your options
                            </a>
                        </div>
                    </div>
                    {imageUrl && (
                        <div className="lg:col-span-5">
                            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 shadow-xl">
                                <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="segment-form-section">
                <LeadForm initial={initial} source={source} />
            </section>

            <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
                <Disclosure />
            </section>

            <Footer />
            <StickyMobileCTA />
        </div>
    );
}
